import { ChevronLeft, ChevronRight, Expand } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import type { TeamPhotoUrls } from "@/types/team"

const PANELS = ["linemen", "specialists", "hero"] as const
type Panel = (typeof PANELS)[number]

const PANEL_LABEL: Record<Panel, string> = {
  linemen: "linemen",
  specialists: "specialists",
  hero: "hero",
}

interface Props {
  urls: TeamPhotoUrls
  teamName: string
}

export function TeamPhotoTriptych({ urls, teamName }: Props) {
  const [openPanel, setOpenPanel] = useState<Panel | null>(null)

  return (
    <>
      <div className="relative mx-auto mb-6 aspect-[2/1] w-full max-w-[800px]">
        <PanelButton
          panel="linemen"
          url={urls.linemen}
          teamName={teamName}
          onOpen={setOpenPanel}
          className={cn(
            "absolute top-1/2 left-0 w-[60%] -translate-y-1/2 scale-[0.85]",
            "md:-rotate-6",
            "z-10",
          )}
          dimmed
        />
        <PanelButton
          panel="specialists"
          url={urls.specialists}
          teamName={teamName}
          onOpen={setOpenPanel}
          className={cn(
            "absolute top-1/2 right-0 w-[60%] -translate-y-1/2 scale-[0.85]",
            "md:rotate-6",
            "z-10",
          )}
          dimmed
        />
        <PanelButton
          panel="hero"
          url={urls.hero}
          teamName={teamName}
          onOpen={setOpenPanel}
          className="absolute inset-x-0 top-1/2 z-20 mx-auto w-[70%] -translate-y-1/2 shadow-xl hover:scale-[1.02]"
          showHoverHint
        />
      </div>

      <Dialog
        open={openPanel !== null}
        onOpenChange={(open) => !open && setOpenPanel(null)}
      >
        <DialogContent
          className="max-h-[90vh] overflow-hidden p-2 sm:max-w-[90vw]"
          showCloseButton
        >
          <DialogTitle className="sr-only">{teamName} team photo</DialogTitle>
          <DialogDescription className="sr-only">
            Triptych team photo with three panels. Use arrow keys or swipe to
            navigate between linemen, specialists, and hero panels.
          </DialogDescription>
          {openPanel && (
            <TriptychCarousel
              urls={urls}
              initialPanel={openPanel}
              teamName={teamName}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

interface PanelButtonProps {
  panel: Panel
  url: string
  teamName: string
  onOpen: (panel: Panel) => void
  className?: string
  dimmed?: boolean
  showHoverHint?: boolean
}

function PanelButton({
  panel,
  url,
  teamName,
  onOpen,
  className,
  dimmed = false,
  showHoverHint = false,
}: PanelButtonProps) {
  return (
    <Button
      variant="ghost"
      type="button"
      aria-label={`View ${PANEL_LABEL[panel]} panel of ${teamName} team photo`}
      onClick={() => onOpen(panel)}
      className={cn(
        "group/panel block h-auto cursor-pointer overflow-hidden border border-[var(--border-heavy)] p-0 transition hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
        className,
      )}
    >
      <img
        src={url}
        alt={`${teamName} ${PANEL_LABEL[panel]}`}
        className={cn(
          "aspect-[1656/1024] w-full object-cover",
          dimmed && "brightness-75 transition group-hover/panel:brightness-100",
        )}
      />
      {showHoverHint && (
        <>
          <div className="pointer-events-none absolute inset-0 bg-black/0 transition-colors group-hover/panel:bg-black/30" />
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/panel:opacity-100">
            <div className="flex items-center gap-2 bg-black/60 px-3 py-1.5 text-sm font-medium text-white">
              <Expand className="size-4" />
              View full image
            </div>
          </div>
        </>
      )}
    </Button>
  )
}

interface CarouselProps {
  urls: TeamPhotoUrls
  initialPanel: Panel
  teamName: string
}

function TriptychCarousel({ urls, initialPanel, teamName }: CarouselProps) {
  const [index, setIndex] = useState(() => PANELS.indexOf(initialPanel))
  const touchStartX = useRef<number | null>(null)

  const goTo = useCallback((next: number) => {
    setIndex(((next % PANELS.length) + PANELS.length) % PANELS.length)
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goTo(index - 1)
      if (e.key === "ArrowRight") goTo(index + 1)
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [index, goTo])

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const dx = e.changedTouches[0].clientX - touchStartX.current
    if (Math.abs(dx) > 40) goTo(dx < 0 ? index + 1 : index - 1)
    touchStartX.current = null
  }

  const currentPanel = PANELS[index]

  return (
    <div className="relative">
      <div
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {PANELS.map((panel) => (
            <img
              key={panel}
              src={urls[panel]}
              alt={`${teamName} ${PANEL_LABEL[panel]}`}
              className="aspect-[1656/1024] w-full flex-shrink-0 object-cover"
            />
          ))}
        </div>
      </div>

      <Button
        variant="ghost"
        type="button"
        aria-label="Previous panel"
        onClick={() => goTo(index - 1)}
        className="absolute top-1/2 left-2 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center bg-black/60 p-0 text-white transition hover:bg-black/80 hover:text-white"
      >
        <ChevronLeft className="size-6" />
      </Button>
      <Button
        variant="ghost"
        type="button"
        aria-label="Next panel"
        onClick={() => goTo(index + 1)}
        className="absolute top-1/2 right-2 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center bg-black/60 p-0 text-white transition hover:bg-black/80 hover:text-white"
      >
        <ChevronRight className="size-6" />
      </Button>

      <div className="mt-2 flex items-center justify-center gap-2">
        {PANELS.map((panel, i) => (
          <Button
            key={panel}
            variant="ghost"
            type="button"
            aria-label={`Go to ${PANEL_LABEL[panel]} panel`}
            onClick={() => goTo(i)}
            className={cn(
              "size-2 cursor-pointer rounded-full p-0 transition hover:bg-white/60",
              currentPanel === panel ? "bg-white" : "bg-white/40",
            )}
          />
        ))}
      </div>
    </div>
  )
}
